import { YStack, XStack, Text, styled } from 'tamagui'
import { copy } from '../../content/copy'

const Section = styled(YStack, {
  name: 'TeamSection',
  paddingHorizontal: '$6',
  paddingVertical: '$16',
  backgroundColor: '$background',

  '$md': {
    paddingHorizontal: '$4',
    paddingVertical: '$10',
  },
})

const Container = styled(YStack, {
  name: 'Container',
  maxWidth: 1100,
  marginHorizontal: 'auto',
  width: '100%',
  gap: '$10',
})

const Header = styled(YStack, {
  name: 'Header',
  gap: '$3',
  marginBottom: '$4',
})

const HeaderRow = styled(XStack, {
  name: 'HeaderRow',
  alignItems: 'flex-start',
  gap: '$4',
})

const SectionNumber = styled(Text, {
  name: 'SectionNumber',
  fontFamily: '$accent',
  fontSize: '$2',
  color: '$textMuted',
  letterSpacing: 2,
})

const SectionTitle = styled(Text, {
  name: 'SectionTitle',
  fontFamily: '$accent',
  fontSize: '$3',
  color: '$orangePrimary',
  letterSpacing: 2,
  textTransform: 'uppercase',
})

const MainHeader = styled(Text, {
  name: 'MainHeader',
  // @ts-ignore
  render: 'h2',
  fontFamily: '$heading',
  fontSize: '$8',
  fontWeight: '600',
  color: '$textPrimary',
  marginTop: '$4',

  '$md': {
    fontSize: '$7',
  },
})

const Description = styled(Text, {
  name: 'Description',
  fontFamily: '$body',
  fontSize: '$5',
  lineHeight: '$6',
  color: '$textSecondary',
  maxWidth: 600,
})

const MembersGrid = styled(XStack, {
  name: 'MembersGrid',
  gap: '$6',
  justifyContent: 'center',
  flexWrap: 'wrap',

  '$md': {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '$10',
  },
})

const MemberCard = styled(YStack, {
  name: 'MemberCard',
  alignItems: 'center',
  gap: '$3',
  maxWidth: 240,
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 0,
  minWidth: 200,

  '$md': {
    flexBasis: 'auto',
    flexShrink: 0,
    maxWidth: 280,
  },
})

const MemberName = styled(Text, {
  name: 'MemberName',
  fontFamily: '$heading',
  fontSize: '$5',
  fontWeight: '600',
  color: '$textPrimary',
  textAlign: 'center',
})

const MemberTitle = styled(Text, {
  name: 'MemberTitle',
  fontFamily: '$body',
  fontSize: '$3',
  color: '$orangePrimary',
  fontWeight: '500',
  textAlign: 'center',
})

const MemberBio = styled(Text, {
  name: 'MemberBio',
  fontFamily: '$body',
  fontSize: '$3',
  color: '$textSecondary',
  textAlign: 'center',
  lineHeight: '$4',
})

function Avatar({ name, image }: { name: string; image: string }) {
  return (
    <div className="team-avatar">
      <div className="team-avatar-inner">
        <img
          src={image}
          alt={name}
          width={96}
          height={96}
          className="team-avatar-img"
        />
        <div className="team-avatar-hue" />
      </div>
      <style>{`
        .team-avatar {
          width: 100px;
          height: 100px;
          border-radius: 50px;
          padding: 2px;
          background: linear-gradient(135deg, #FF6300 0%, #FF9D3A 50%, #FFE0AD 100%);
        }
        .team-avatar-inner {
          position: relative;
          width: 96px;
          height: 96px;
          border-radius: 48px;
          overflow: hidden;
        }
        .team-avatar-img {
          width: 96px;
          height: 96px;
          border-radius: 48px;
          object-fit: cover;
          display: block;
        }
        .team-avatar-hue {
          position: absolute;
          inset: 0;
          border-radius: 48px;
          background: linear-gradient(135deg, rgba(255, 99, 0, 0.15) 0%, rgba(255, 157, 58, 0.08) 100%);
          mix-blend-mode: color;
          pointer-events: none;
        }
      `}</style>
    </div>
  )
}

export function TeamSection() {
  const { sectionNumber, sectionTitle, header, description, members } = copy.team

  return (
    <Section id="team">
      <Container>
        <Header>
          <HeaderRow>
            <SectionNumber>{sectionNumber}</SectionNumber>
            <SectionTitle>{sectionTitle}</SectionTitle>
          </HeaderRow>
          <MainHeader>{header}</MainHeader>
          <Description>{description}</Description>
        </Header>

        <MembersGrid>
          {members.map((member) => (
            <MemberCard key={member.name}>
              <Avatar name={member.name} image={member.image} />
              <MemberName>{member.name}</MemberName>
              <MemberTitle>{member.title}</MemberTitle>
              <MemberBio>{member.bio}</MemberBio>
            </MemberCard>
          ))}
        </MembersGrid>
      </Container>
    </Section>
  )
}
